import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import { styles } from '../../../../styles/themes/style';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import {
  ICampaignVariableFallbacks,
  renderCampaignTemplate,
} from '../../../../utils/campaignTemplateVariables';

/**
 * "Generate message" — the AI writes a draft, you read it as the recipient would, and
 * take it or leave it.
 *
 * Two different texts are in play and confusing them is the whole risk here:
 *
 *  - what is SHOWN is rendered for a sample lead, so {{firstName}} reads as "Alex" and
 *    the draft can be judged as a message rather than as a template;
 *  - what is TAKEN is the raw draft, tokens intact, because it is saved once and
 *    substituted per recipient at send time.
 *
 * Showing the raw tokens instead made people "fix" them by hand; storing the rendered
 * version would greet every lead as Alex.
 */

export type GenerateStepType =
  | 'message' | 'post_comment' | 'connection_request' | 'email' | 'email_subject';

interface Props {
  show: boolean;
  onHide: () => void;
  /**
   * 'generate' asks the AI for a draft and offers to take it.
   * 'preview' just renders what is already in the field for a sample lead — no AI
   * call, and nothing to accept, because the text is already the text.
   */
  mode?: 'generate' | 'preview';
  /** Receives the RAW draft — tokens unresolved. Unused in 'preview'. */
  onUse: (text: string, subject?: string) => void;
  /** In 'generate', what to write from. In 'preview', what to show. */
  instruction: string;
  stepType: GenerateStepType;
  language: string;
  campaignDescription?: string;
  postText?: string;
  variableFallbacks?: ICampaignVariableFallbacks;

  /** Who this goes out as — drawn at the top, the way the recipient sees it. */
  senderName?: string;
  /** The mailbox address for email, or the channel for a LinkedIn step. */
  senderSubline?: string;
  /** Shown under an email body, because it really is appended on send. */
  signatureHtml?: string;
  /** The step's subject, so an email / InMail preview reads with a subject line. */
  subject?: string;
  /**
   * Also generate (and preview) a subject with the body — email always does this;
   * InMail / Open Profile passes it explicitly.
   */
  withSubject?: boolean;
}

/**
 * The sign-off placeholder. Kept character-for-character identical to the executor's
 * pattern (EmailStepService), because a preview that recognises the token in more — or
 * fewer — places than the send path is worse than no preview at all.
 *
 * Tags and entity spaces are allowed anywhere inside it: what reaches this dialog is
 * already flattened to text, but what reaches the executor is markup, and a
 * contenteditable readily leaves a <span> in the middle of a token.
 */
const TOKEN_GAP = '(?:<[^>]*>|&nbsp;|\\s)*';
const SIGNATURE_TOKEN = new RegExp(
  `\\{${TOKEN_GAP}\\{${TOKEN_GAP}${'signature'.split('').join(TOKEN_GAP)}${TOKEN_GAP}\\}${TOKEN_GAP}\\}`,
  'gi',
);

/** The lead the preview is written for. Mirrors the composer's own sample. */
const SAMPLE_LEAD = {
  firstName: 'Alex',
  lastName: 'Chen',
  name: 'Alex Chen',
  company: 'Acme',
  title: 'VP of Sales',
};

const ACCEPT_LABEL: Record<GenerateStepType, string> = {
  message:            'Use this message',
  post_comment:       'Use this comment',
  connection_request: 'Use this note',
  email:              'Use this email',
  email_subject:      'Use this subject',
};

function initialsOf(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

const CampaignGeneratePopup: React.FC<Props> = ({
  show, onHide, mode = 'generate', onUse, instruction, stepType, language,
  campaignDescription, postText, variableFallbacks,
  senderName, senderSubline, signatureHtml, subject, withSubject = false,
}) => {
  const { previewCampaignAiMessage } = useCampaignsRequest();

  const [text, setText] = useState('');
  /** Generated alongside the body for an email — never asked for on its own. */
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const wantsSubject = stepType === 'email' || withSubject;

  /** One call. Returns the text, or throws with the reason the server gave. */
  const ask = useCallback(async (type: GenerateStepType, content: string) => {
    const res: any = await previewCampaignAiMessage({
      stepType: type,
      content,
      language,
      ...(postText ? { postText } : {}),
      ...(campaignDescription ? { campaignDescription } : {}),
      ...(variableFallbacks ? { variableFallbacks } : {}),
    });
    // `all: true` on the request, so a refusal keeps its own message instead of
    // collapsing into a generic failure.
    const body = res?.data ?? res;
    if (body?.generated && body?.content) return String(body.content);
    throw new Error(body?.error || res?.message || 'Could not generate anything just now.');
    // previewCampaignAiMessage is rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, postText, campaignDescription, variableFallbacks]);

  const run = useCallback(async () => {
    const prompt = instruction.trim();
    if (!prompt) { setError('Write what you want the AI to produce first.'); return; }

    setLoading(true);
    setError('');
    setText('');
    setGeneratedSubject('');

    try {
      const body = await ask(stepType, prompt);
      setText(body);

      /*
        An email's subject comes with its body, from the same click.

        It is asked for second, from the body that was just written rather than from
        the user's instruction — a subject has to describe the email that exists, and
        one generated in parallel from the same brief routinely promised something the
        body never said. The extra call is worth a pair that matches.

        Failing here is not fatal: the body is good, and the subject field is still
        editable, so the draft is offered with whatever subject was already there.
      */
      if (wantsSubject) {
        try {
          const line = await ask(
            'email_subject',
            `Write the subject line for this message:\n"""\n${body}\n"""`,
          );
          setGeneratedSubject(line.replace(/\s+/g, ' ').trim());
        } catch {
          setGeneratedSubject('');
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Could not generate anything just now.');
    } finally {
      setLoading(false);
    }
  }, [instruction, stepType, ask, wantsSubject]);

  // Generate on open, so the button is one click rather than two. In 'preview' the
  // field's own text is what we show, and calling the AI would be both wasteful and
  // wrong — it would preview something other than what is about to be sent.
  useEffect(() => {
    if (!show) return;
    if (mode === 'preview') {
      setLoading(false);
      setError(instruction.trim() ? '' : 'Nothing written yet.');
      setText(instruction);
      /*
        Clear the generated subject. It outranks the field's own when rendering, and
        it survived the dialog closing — so after one generate, every later preview
        showed that AI subject above whatever had actually been typed since.
      */
      setGeneratedSubject('');
      return;
    }
    run();
    // Only when the dialog opens — re-running on every keystroke behind it would
    // burn calls on text the user is still writing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode]);

  /** Read as the recipient would — the draft is judged as a message, not a template. */
  const shownBody = useMemo(
    () => renderCampaignTemplate(text, SAMPLE_LEAD, '', variableFallbacks ?? {}),
    [text, variableFallbacks],
  );
  /* The freshly generated subject wins over whatever is in the field — it is the one
     written for the body being shown. */
  const shownSubject = useMemo(
    () => renderCampaignTemplate(generatedSubject || subject || '', SAMPLE_LEAD, '', variableFallbacks ?? {}),
    [generatedSubject, subject, variableFallbacks],
  );

  /*
    Where the signature lands, mirroring what the executor does at send time.

    {{signature}} decides whether the sign-off appears at all, and where. Nothing is
    appended to an email that does not place the token — the preview has to agree with
    the send path on that, or it stops being a preview.

    Split rather than string-replaced because the body renders as text and the
    signature is markup: the two parts are rendered around it.
  */
  const bodyParts = useMemo(() => shownBody.split(SIGNATURE_TOKEN), [shownBody]);

  const ready = !loading && !error && !!text.trim();

  return (
    <StyledModal show={show} onHide={onHide} width="620px" backdrop>
      <Body>
        <Header>
          {/* "Preview" in both modes — the design titles the post-Generate dialog
              that way, and what it shows really is a preview either way. */}
          <Title>Preview</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" style={{ display: 'block' }}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseBtn>
        </Header>

        <Sheet>
          {!!senderName && (
            <SenderRow>
              <Avatar aria-hidden>{initialsOf(senderName)}</Avatar>
              <div>
                <SenderName>{senderName}</SenderName>
                {!!senderSubline && <SenderSub>{senderSubline}</SenderSub>}
              </div>
            </SenderRow>
          )}

          {loading && <Muted>Writing…</Muted>}
          {!loading && !!error && <ErrText>{error}</ErrText>}

          {ready && (
            <>
              {/* Subject for email and InMail — a subject-line draft IS the body here. */}
              {wantsSubject && !!shownSubject.trim() && (
                <Subject>{shownSubject}</Subject>
              )}
              {bodyParts.map((part, idx) => (
                <React.Fragment key={idx}>
                  <Message>{part}</Message>
                  {/* Between the parts is exactly where the token was. */}
                  {idx < bodyParts.length - 1 && stepType === 'email' && (
                    signatureHtml
                      // The sender's own stored markup, already sanitized server-side.
                      ? <Signature $inline dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                      : <SignaturePlaceholder>Your signature goes here.</SignaturePlaceholder>
                  )}
                </React.Fragment>
              ))}

              {/* Nothing appended here. A body with no {{signature}} sends with no
                  signature, so a preview that added one would be showing an email
                  nobody is going to receive. */}
            </>
          )}
        </Sheet>

        {/* No footnote. The preview already reads as a finished message, and a line
            explaining the mechanics underneath undid that. */}

        <Actions>
          {mode === 'preview' ? (
            <PrimaryBtn type="button" onClick={onHide}>Close</PrimaryBtn>
          ) : (
            <>
              <GhostBtn type="button" onClick={onHide}>Cancel</GhostBtn>
              <GhostBtn type="button" disabled={loading} onClick={run}>
                {loading ? 'Writing…' : 'Regenerate'}
              </GhostBtn>
              {/* Hands back `text`, not `shownBody` — see the note at the top of the file. */}
              <PrimaryBtn type="button" disabled={!ready} onClick={() => { onUse(text, generatedSubject || undefined); onHide(); }}>
                {ACCEPT_LABEL[stepType]}
              </PrimaryBtn>
            </>
          )}
        </Actions>
      </Body>
    </StyledModal>
  );
};

export default CampaignGeneratePopup;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Body = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  padding: 20px 22px 18px;
`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`;

const Title = styled.div`
  font-family: NunitoSans; font-size: 17px; font-weight: 800; color: #0f172a;
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  border: none; background: none; cursor: pointer; padding: 2px;
  color: #94a3b8;
  &:hover { color: #475569; }
`;

/* The message, laid out as the recipient receives it rather than as a form field. */
const Sheet = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  min-height: 180px; max-height: 52vh; overflow-y: auto;
  padding: 2px;
`;

const SenderRow = styled.div`
  display: flex; align-items: center; gap: 10px;
`;

const Avatar = styled.div`
  flex: 0 0 auto;
  width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: ${styles.colors.primary100}; color: ${ACCENT};
  font-family: NunitoSans; font-size: 13px; font-weight: 800;
`;

const SenderName = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 800; color: #0f172a;
`;

const SenderSub = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const Subject = styled.div`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: #0f172a;
`;

const Message = styled.div`
  white-space: pre-wrap;
  font-family: NunitoSans, sans-serif; font-size: 13px; line-height: 1.65; color: #334155;
`;

const SignaturePlaceholder = styled.div`
  padding: 8px 10px; border: 1px dashed #cbd5e1; border-radius: 6px;
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

/*
 * Set in the body's own type, not in small grey.
 *
 * At 12px/#64748b under a rule it read as a footnote — one tester took it for an error
 * message printed under the preview. It is neither: it is the last paragraph of the
 * email, and the recipient will see it in the same type as everything above it.
 */
const Signature = styled.div<{ $inline?: boolean }>`
  padding-top: ${p => (p.$inline ? '0' : '10px')};
  font-family: NunitoSans, sans-serif; font-size: 13px; line-height: 1.65; color: #334155;
  img { max-width: 100%; height: auto; }
  a { color: ${ACCENT}; }
`;

const Muted = styled.div`
  font-family: NunitoSans; font-size: 13px; color: #94a3b8;
`;

const ErrText = styled.div`
  font-family: NunitoSans; font-size: 13px; line-height: 1.5; color: #dc2626;
`;


const Actions = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding-top: 4px; border-top: 1px solid #eef1f6;
`;

const GhostBtn = styled.button`
  border: none; background: none; cursor: pointer; padding: 9px 10px;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #64748b;
  &:hover:not(:disabled) { color: #334155; }
  &:disabled { opacity: .6; cursor: default; }
`;

const PrimaryBtn = styled.button`
  border: none; border-radius: 8px; padding: 10px 16px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  &:disabled { opacity: .5; cursor: default; }
`;
